import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import { StringGroupVersionSummary, TranslationStatus } from '../types';
import styles from '../StringGroupDetail.module.scss';

const NOT_AVAILABLE = '—';

interface VersionHistoryPanelProps {
  visible: boolean;
  stringGroupName: string;
  versions: StringGroupVersionSummary[] | null;
  loading: boolean;
  error?: Error | null;
  /**
   * When false, the Rollback action is disabled (grayed out) because the group
   * is owned by a non-LangHub client. Diff/View stay available (read-only).
   */
  canEdit?: boolean;
  /**
   * Whether the permission answer is still in flight. Gated controls show a skeleton rather
   * than a disabled state, so they never claim a missing permission before it is known.
   */
  permissionsLoading?: boolean;
  onClose: () => void;
  onDiff: (version: number) => void;
  onView: (version: number) => void;
  onRollback: (version: number) => void;
}

function formatDate(timestamp: number): string {
  if (!timestamp) {
    return NOT_AVAILABLE;
  }
  const date = new Date(timestamp).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
  return date.toString();
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  visible,
  stringGroupName,
  versions,
  loading,
  error,
  canEdit = true,
  permissionsLoading = false,
  onClose,
  onDiff,
  onView,
  onRollback,
}) => {
  if (!visible) {
    return null;
  }

  const latestVersion =
    versions && versions.length > 0
      ? Math.max(...versions.map((v) => v.version))
      : 0;

  return (
    <Container
      header={
        <div className={styles.versionHistoryHeader}>
          <h3 className={styles.versionHistoryTitle}>
            Version History &mdash; {stringGroupName}
          </h3>
          <Button
            variant="normal"
            onClick={onClose}
            ariaLabel="Close version history panel"
          >
            ✕ Close
          </Button>
        </div>
      }
    >
      <div className={styles.versionHistorySubtitle}>
        Showing last 25 versions
      </div>

      {loading && <Spinner size="normal" />}

      {!loading && error && (
        <Alert type="error">
          Failed to load version history: {error.message}
        </Alert>
      )}

      {!loading && !error && versions?.length === 0 && (
        <Box textAlign="center" color="text-body-secondary" padding="l">
          No versions found for this string group.
        </Box>
      )}

      {!loading && !error && versions && versions.length > 0 && (
        <SpaceBetween size="xs">
          {versions.map((v) => {
            const vNum = v.version;
            return (
              <div key={v.version} className={styles.versionItem}>
                <span
                  className={styles.versionBadge}
                  aria-label={`Version ${v.version}`}
                >
                  v{v.version}
                </span>
                <div className={styles.versionSummary}>
                  <strong className={styles.versionDescription}>
                    {v.changeSummary?.description ?? `Version ${v.version}`}
                    {v.translationStatus === TranslationStatus.Cancelled && (
                      <>
                        {' '}
                        <Badge color="grey">Cancelled</Badge>
                      </>
                    )}
                  </strong>
                  <div className={styles.versionMeta}>
                    {formatDate(v.updatedOn)} &middot; by {v.updatedBy}
                  </div>
                </div>
                <div className={styles.versionActions}>
                  {/* Latest version: only Diff */}
                  {vNum === latestVersion && vNum !== 1 && (
                    <Button
                      variant="normal"
                      onClick={() => onDiff(vNum)}
                      ariaLabel={`Show diff for version ${v.version}`}
                    >
                      Diff
                    </Button>
                  )}
                  {/* Middle versions: Diff + View + Rollback */}
                  {vNum !== latestVersion && vNum !== 1 && (
                    <>
                      <Button
                        variant="normal"
                        onClick={() => onDiff(vNum)}
                        ariaLabel={`Show diff for version ${v.version}`}
                      >
                        Diff
                      </Button>
                      <Button
                        variant="normal"
                        onClick={() => onView(vNum)}
                        ariaLabel={`View version ${v.version} snapshot`}
                      >
                        View
                      </Button>
                      {permissionsLoading ? (
                        <Skeleton
                          display="inline-block"
                          height="32px"
                          width="85px"
                        />
                      ) : (
                        <Button
                          variant="normal"
                          disabled={!canEdit}
                          onClick={() => onRollback(vNum)}
                          ariaLabel={`Rollback to version ${v.version}`}
                        >
                          Rollback
                        </Button>
                      )}
                    </>
                  )}
                  {/* v1 (initial): only View */}
                  {vNum === 1 && (
                    <Button
                      variant="normal"
                      onClick={() => onView(vNum)}
                      ariaLabel={`View version ${v.version} snapshot`}
                    >
                      View
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </SpaceBetween>
      )}
    </Container>
  );
};

export default VersionHistoryPanel;
